/**
 * Generates a MedChron export (PDF/DOCX) and downloads the resulting file. `export()` sends
 * `customization_options` only for a one-off export that overrides them; omitted, the server
 * applies the caller's own saved preferences.
 */
import { ChronologyViewType } from "../components/MedicalChronology/Visits/types";
import { MED_CHRON_EXPORT_LAYOUT } from "../constants/medChronExport";
import type { ExportFormat, MedChronExportCustomizationOptions } from "../hooks/useExportCase";
import { useUserCustomizationPreferencesStore } from "../store/userCustomizationPreferencesStore";
import { toSlug } from "../utils/stringUtils";
import type { APIInstance } from "./apiService";
import { generateSignedUrlForDownload } from "./supabaseClient";

interface ExportResponseData {
  status: "success" | "pending";
  data?: { bucket: string; path: string };
}

const MAX_TOTAL_TIME = 5 * 60 * 1000;
const INITIAL_DELAY = 1000;
const MAX_DELAY = 10 * 1000;

export type MedChronExportCustomizationApiPayload = {
  data_customization: { collapse_therapy_visits: boolean };
  view_customization: Record<string, string | boolean | null>;
};

type MedChronExportRequestBody = {
  case_id: string;
  document_format: ExportFormat;
  customization_options?: MedChronExportCustomizationApiPayload;
};

export function buildMedChronExportCustomizationApiPayload(
  customization_options: MedChronExportCustomizationOptions
): MedChronExportCustomizationApiPayload {
  const { dataCustomization, viewCustomization } = customization_options;
  return {
    data_customization: {
      collapse_therapy_visits: dataCustomization.collapseTherapyVisits,
    },
    view_customization: {
      date_format: MED_CHRON_EXPORT_LAYOUT.dateFormat,
      visit_heading_text_color: MED_CHRON_EXPORT_LAYOUT.visitHeadingTextColor,
      visit_heading_underline: MED_CHRON_EXPORT_LAYOUT.visitHeadingUnderline,
      visit_heading_date_location_separator:
        MED_CHRON_EXPORT_LAYOUT.visitHeadingDateLocationSeparator,
      chronology_sort_field: viewCustomization.chronologySortField,
      chronology_sort_direction: viewCustomization.chronologySortDirection,
      chronology_view_group_by:
        viewCustomization.chronologyViewGroupBy === ChronologyViewType.DATE
          ? null
          : viewCustomization.chronologyViewGroupBy,
    },
  };
}

/** Current user preferences from the store plus default export layout fields. */
export function getMedChronExportCustomizationPayload(): MedChronExportCustomizationApiPayload {
  const {
    collapseTherapyVisits,
    chronologyViewGroupBy,
    chronologySortField,
    chronologySortDirection,
  } = useUserCustomizationPreferencesStore.getState();
  return buildMedChronExportCustomizationApiPayload({
    dataCustomization: { collapseTherapyVisits },
    viewCustomization: { chronologyViewGroupBy, chronologySortField, chronologySortDirection },
  });
}

export class MedChronExportService {
  private api: APIInstance;

  constructor(api: APIInstance) {
    this.api = api;
  }

  async export(
    caseId: string,
    format: ExportFormat,
    caseName?: string | null,
    customizationOptions?: MedChronExportCustomizationApiPayload
  ): Promise<void> {
    const requestBody: MedChronExportRequestBody = {
      case_id: caseId,
      document_format: format,
      // Omitted entirely for a plain export: the server then applies the caller's own saved
      // preferences. Sent only for a one-off export whose options differ from those.
      ...(customizationOptions ? { customization_options: customizationOptions } : {}),
    };

    const response = await this.api.post<ExportResponseData>("/med_chron/export", requestBody);

    if (response.data.status === "success") {
      await this.download(response.data, format, caseName);
      return;
    }

    if (response.data.status === "pending") {
      const completed = await this.poll(requestBody);
      await this.download(completed, format, caseName);
      return;
    }

    throw new Error("Unexpected response status from server");
  }

  private async poll(requestBody: MedChronExportRequestBody): Promise<ExportResponseData> {
    const startTime = Date.now();
    let attempt = 0;
    let delay = INITIAL_DELAY;

    while (Date.now() - startTime < MAX_TOTAL_TIME) {
      await new Promise((resolve) => setTimeout(resolve, delay));

      const response = await this.api.post<ExportResponseData>("/med_chron/export", requestBody);

      if (response.data.status === "success") return response.data;

      attempt++;
      delay = Math.min(INITIAL_DELAY * Math.pow(2, attempt), MAX_DELAY);
    }

    throw new Error(
      `${requestBody.document_format.toUpperCase()} generation timed out. Please try again later.`
    );
  }

  private async download(
    responseData: ExportResponseData,
    format: ExportFormat,
    caseName?: string | null
  ): Promise<void> {
    const { bucket, path } = responseData?.data || {};
    if (!path || !bucket) throw new Error("Invalid export data received");

    const extByFormat: Record<ExportFormat, string> = { pdf: "pdf", docx: "docx" };
    const pathExt = path.split(".").pop();
    const ext = pathExt && pathExt !== path ? pathExt : extByFormat[format];
    const safeName = caseName ? toSlug(caseName) : "medical-chronology";
    const fileName = `${safeName}.${ext}`;

    // Passing fileName sets Content-Disposition: attachment; filename="..." in the signed URL,
    // which the browser honors even for cross-origin S3/CloudFront URLs.
    const signedUrl = await generateSignedUrlForDownload(bucket, path, 60, fileName);
    if (!signedUrl) throw new Error("Failed to generate signed URL");

    const link = document.createElement("a");
    link.href = signedUrl;
    link.download = fileName;

    try {
      document.body.appendChild(link);
      link.click();
    } finally {
      try {
        document.body.removeChild(link);
      } catch {
        // ignore if already removed
      }
    }
  }
}
